interface TaxBracket {
  rate: number;
  cap: number;
}

interface LTCGThresholds {
  zeroPercent: number;
  fifteenPercent: number;
}

// Federal tax brackets (2024)
const FEDERAL_TAX_BRACKETS: Record<'single' | 'married', TaxBracket[]> = {
  single: [
    { rate: 0.1, cap: 11600 },
    { rate: 0.12, cap: 47150 },
    { rate: 0.22, cap: 100525 },
    { rate: 0.24, cap: 191950 },
    { rate: 0.32, cap: 243725 },
    { rate: 0.35, cap: 609350 },
    { rate: 0.37, cap: Infinity },
  ],
  married: [
    { rate: 0.1, cap: 23200 },
    { rate: 0.12, cap: 94300 },
    { rate: 0.22, cap: 201050 },
    { rate: 0.24, cap: 383900 },
    { rate: 0.32, cap: 487450 },
    { rate: 0.35, cap: 731200 },
    { rate: 0.37, cap: Infinity },
  ],
};

// Long-term capital gains thresholds (2024)
const LTCG_THRESHOLDS: Record<'single' | 'married', LTCGThresholds> = {
  single: { zeroPercent: 47025, fifteenPercent: 518900 },
  married: { zeroPercent: 94050, fifteenPercent: 583750 },
};

// State tax rates approximations for all states (simplified effective rates)
// Note: These rates are approximate and may not capture the complexity of state tax systems.
export const STATE_TAX_RATES: { [state: string]: number } = {
  AL: 0.05,
  AK: 0.0,
  AZ: 0.025,
  AR: 0.065,
  CA: 0.133,
  CO: 0.0463,
  CT: 0.069,
  DE: 0.066,
  FL: 0.0,
  GA: 0.0575,
  HI: 0.11,
  ID: 0.065,
  IL: 0.0495,
  IN: 0.0323,
  IA: 0.085,
  KS: 0.057,
  KY: 0.05,
  LA: 0.06,
  ME: 0.071,
  MD: 0.0575,
  MA: 0.051,
  MI: 0.0425,
  MN: 0.0785,
  MS: 0.05,
  MO: 0.054,
  MT: 0.069,
  NE: 0.0684,
  NV: 0.0,
  NH: 0.0, // Applies only to dividends and interest; wage income is not taxed
  NJ: 0.0637,
  NM: 0.049,
  NY: 0.0685,
  NC: 0.05499,
  ND: 0.022, // Approximate effective rate
  OH: 0.041,
  OK: 0.05,
  OR: 0.09,
  PA: 0.0307,
  RI: 0.059,
  SC: 0.07,
  SD: 0.0,
  TN: 0.0, // Wage income is not taxed; dividends/interest at 1% not captured here
  TX: 0.0,
  UT: 0.0495,
  VT: 0.065,
  VA: 0.0575,
  WA: 0.0,
  WV: 0.06,
  WI: 0.057,
  WY: 0.0,
  DC: 0.085,
};

function calculateOrdinaryFederalTax(
  taxableIncome: number,
  filingStatus: 'single' | 'married',
): number {
  const brackets = FEDERAL_TAX_BRACKETS[filingStatus];

  let tax = 0;
  let previousCap = 0;

  for (const bracket of brackets) {
    if (taxableIncome > bracket.cap) {
      tax += (bracket.cap - previousCap) * bracket.rate;
      previousCap = bracket.cap;
    } else {
      tax += (taxableIncome - previousCap) * bracket.rate;
      break;
    }
  }
  return tax;
}

interface TaxCalculationParams {
  income: number; // regular income (wages, salary, etc.)
  shortTermCapitalGains: number; // gains taxed as ordinary income
  longTermCapitalGains: number; // gains taxed at preferential LTCG rates
  filingStatus: 'single' | 'married';
  state: string; // e.g. 'CA', 'TX'
}

interface TaxCalculationResult {
  federalTax: number;
  stateTax: number;
  totalTax: number;
  capitalGainsTax: number;
}

export function calculateTax(
  params: TaxCalculationParams,
): TaxCalculationResult {
  const {
    income,
    shortTermCapitalGains,
    longTermCapitalGains,
    filingStatus,
    state,
  } = params;

  const totalIncome = income + shortTermCapitalGains + longTermCapitalGains;

  const federalOrdinaryTax = calculateOrdinaryFederalTax(
    totalIncome,
    filingStatus,
  );

  const thresholds = LTCG_THRESHOLDS[filingStatus];
  let ltRate: number;
  if (totalIncome < thresholds.zeroPercent) {
    ltRate = 0;
  } else if (totalIncome < thresholds.fifteenPercent) {
    ltRate = 0.15;
  } else {
    ltRate = 0.2;
  }
  const federalLTCGTax = longTermCapitalGains * ltRate;

  const shortTermTaxRate =
    calculateOrdinaryFederalTax(totalIncome, filingStatus) / totalIncome;
  const federalShortTermTax = shortTermCapitalGains * shortTermTaxRate;

  const federalTax = federalOrdinaryTax + federalLTCGTax;

  const stateRate = STATE_TAX_RATES[state.toUpperCase()] || 0;
  const stateTax = totalIncome * stateRate;

  const stateCapitalGainsTax =
    (shortTermCapitalGains + longTermCapitalGains) * stateRate;

  const totalTax = federalTax + stateTax;

  const capitalGainsTax =
    federalLTCGTax + federalShortTermTax + stateCapitalGainsTax;

  return {
    federalTax,
    stateTax,
    totalTax,
    capitalGainsTax,
  };
}
